// context/UserContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { saveUserLocation } from '../firebase/user';

const UserContext = createContext();

const getInitialState = () => {
  try {
    const storedLocation = localStorage.getItem('userLocation');
    const storedFavorites = localStorage.getItem('userFavorites');
    
    return {
      location: storedLocation ? JSON.parse(storedLocation) : null,
      postalCode: storedLocation ? JSON.parse(storedLocation).postalCode || '' : '',
      favorites: storedFavorites ? new Set(JSON.parse(storedFavorites)) : new Set(),
    };
  } catch (e) {
    console.error('Failed to parse stored data:', e);
    return { location: null, postalCode: '', favorites: new Set() };
  }
};

export function UserProvider({ children }) {
  const { user: authUser } = useAuth();
  const [userData, setUserData] = useState(getInitialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync with Firebase when authenticated
  useEffect(() => {
    if (!authUser?.uid) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', authUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setUserData(prev => ({
            ...prev,
            uid: docSnapshot.id,
            ...data,
            // Keep favorites as Set, merge Firebase favorites
            favorites: new Set([
              ...prev.favorites,
              ...(data.favorites?.map(f => f.productId) || [])
            ]),
          }));
        }
        setError(null);
      },
      (err) => {
        console.error('Error fetching user data:', err);
        setError(err.message);
      }
    );

    return () => unsubscribe();
  }, [authUser?.uid]);

  // Persist favorites to localStorage when they change
  useEffect(() => {
    if (userData?.favorites) {
      localStorage.setItem('userFavorites', JSON.stringify([...userData.favorites]));
    }
  }, [userData?.favorites]);

  const updateLocation = useCallback(async (locationData) => {
    setUserData(prev => ({
      ...prev,
      location: locationData,
      postalCode: locationData.postalCode || '',
    }));
    localStorage.setItem('userLocation', JSON.stringify(locationData));

    if (authUser?.uid) {
      try {
        await saveUserLocation(authUser.uid, locationData);
      } catch (err) {
        console.error('Failed to save location to Firebase:', err);
        throw err;
      }
    }
  }, [authUser?.uid]);

  const toggleFavorite = useCallback(async (product) => {
    const isFav = userData?.favorites?.has(product.id);

    if (isFav) {
      // Just remove locally, don't touch Firebase
      setUserData(prev => {
        const next = new Set(prev.favorites);
        next.delete(product.id);
        return { ...prev, favorites: next };
      });
    } else {
      // Add locally
      setUserData(prev => {
        const next = new Set(prev.favorites);
        next.add(product.id);
        return { ...prev, favorites: next };
      });

      // Also save to Firebase if authenticated (fire and forget)
      if (authUser?.uid) {
        try {
          const userRef = doc(db, 'users', authUser.uid);
          await updateDoc(userRef, {
            favorites: arrayUnion({
              productId: product.id,
              category: product.category || null,
              subcategory: product.subcategory || null,
              name: product.name || null,
            })
          });
        } catch (err) {
          console.error('Failed to save favorite to Firebase:', err);
          // Don't rollback - keep it local
        }
      }
    }
  }, [authUser?.uid, userData?.favorites]);

  const isFavorite = useCallback((productId) => {
    return userData?.favorites?.has(productId) || false;
  }, [userData?.favorites]);

  const value = {
    userData,
    loading,
    error,
    updateLocation,
    toggleFavorite,
    isFavorite,
    getFavorites: () => [...(userData?.favorites || [])],
    getLocation: () => userData?.location || null,
    getCity: () => userData?.location?.city || null,
    getPostalCode: () => userData?.postalCode || userData?.location?.postalCode || '',
    getRoles: () => userData?.roles || [],
    getCart: () => userData?.cart || [],
    getPurchases: () => userData?.purchases || [],
    isAuthenticated: !!authUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}